import { useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLoading } from 'react-simplikit';
import { toast } from 'sonner';

import { BOARD_LIMIT, POSTS_PER_BOARD_LIMIT } from '@/entities/board';
import { useBoardsPosts } from '@/features/boards/hooks/use-boards-posts';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import { Input } from '@/shared/components/shadcn/input';
import { RadioGroup, RadioGroupItem } from '@/shared/components/shadcn/radio-group';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { cn } from '@/shared/utils';

type Props = {
  isOpen: boolean;
  close: () => void;
  postIds: string[];
  onSaved?: () => void | Promise<void>;
};

export function SaveToBoardsDialog({ isOpen, close, postIds, onSaved }: Props) {
  const navigate = useNavigate();
  const { workspaceId } = useWorkspaceId();
  const { lists, createList, addPostsToLists } = useBoardsPosts();
  const [selectedListId, setSelectedListId] = useState('');
  const [newListTitle, setNewListTitle] = useState('');
  const [isCreateSectionOpen, setIsCreateSectionOpen] = useState(false);
  const [isLoading, startLoading] = useLoading();
  const wasOpenRef = useRef(false);

  const postCount = postIds.length || 1;
  const listCountText = useMemo(() => `${lists.length}/${BOARD_LIMIT} boards`, [lists.length]);
  const isCreateMode = newListTitle.trim().length > 0;
  const isCreateOverLimit = isCreateMode && postIds.length > POSTS_PER_BOARD_LIMIT;
  const overflowBy = Math.max(postIds.length - POSTS_PER_BOARD_LIMIT, 0);
  const fullBoardIdSet = useMemo(
    () =>
      new Set(
        lists.filter((list) => list.post_count >= POSTS_PER_BOARD_LIMIT).map((list) => list.id),
      ),
    [lists],
  );
  const firstAvailableBoardId = useMemo(
    () => lists.find((list) => !fullBoardIdSet.has(list.id))?.id ?? '',
    [lists, fullBoardIdSet],
  );
  const hasAvailableBoard = firstAvailableBoardId.length > 0;

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    if (justOpened) {
      setSelectedListId(firstAvailableBoardId);
      setNewListTitle('');
      setIsCreateSectionOpen(lists.length === 0);
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, firstAvailableBoardId, lists.length]);

  const closeDialog = () => {
    setSelectedListId('');
    setNewListTitle('');
    setIsCreateSectionOpen(false);
    close();
  };

  const openBoard = (boardId: string) => {
    navigate({
      to: '/workspace/$workspaceId/boards/$boardId',
      params: { workspaceId, boardId },
    });
  };

  const handleSaveToSelectedBoard = async (listId: string) => {
    if (!listId) return;
    const targetList = lists.find((list) => list.id === listId);
    const savedCount = await addPostsToLists([listId], postIds);
    if (savedCount > 0) {
      toast.success(
        `Saved ${savedCount} post${savedCount > 1 ? 's' : ''} to ${targetList?.title ?? 'board'}`,
        {
          action: {
            label: 'View board',
            onClick: () => openBoard(listId),
          },
        },
      );
    }

    if (savedCount > 0) {
      await onSaved?.();
      closeDialog();
    }
  };

  const handleCreateAndSave = async () => {
    if (!newListTitle.trim()) return;
    if (postIds.length > POSTS_PER_BOARD_LIMIT) return;
    const createResult = await createList(newListTitle);
    if (!createResult.ok) {
      toast.error(createResult.reason);
      return;
    }
    const savedCount = await addPostsToLists([createResult.listId], postIds);
    if (savedCount > 0) {
      toast.success(`Created board and saved ${savedCount} post${savedCount > 1 ? 's' : ''}`, {
        action: {
          label: 'View board',
          onClick: () => openBoard(createResult.listId),
        },
      });
    }

    if (savedCount > 0) {
      await onSaved?.();
      closeDialog();
    }
  };

  const handleSubmit = async () => {
    if (isLoading) return;

    await startLoading(
      (async () => {
        if (newListTitle.trim().length > 0) {
          await handleCreateAndSave();
          return;
        }
        if (selectedListId) {
          await handleSaveToSelectedBoard(selectedListId);
        }
      })(),
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save to Boards</DialogTitle>
          <DialogDescription>
            Add {postCount} selected post{postCount > 1 ? 's' : ''} to a board.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {lists.length > 0 && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-foreground text-sm font-medium">Select board</h2>
                <span className="text-muted-foreground text-xs">{listCountText}</span>
              </div>
              <RadioGroup
                value={selectedListId}
                onValueChange={(id) => {
                  setSelectedListId(id);
                  setNewListTitle('');
                }}
                className="max-h-64 gap-0 space-y-1 overflow-auto"
              >
                {lists.map((list, index) => (
                  <label
                    key={`board-${list.id}`}
                    htmlFor={`board-${list.id}`}
                    className={cn(
                      'border-border flex cursor-pointer items-center gap-3 rounded-md border p-3',
                      fullBoardIdSet.has(list.id) && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <RadioGroupItem
                      id={`board-${list.id}`}
                      value={list.id}
                      disabled={fullBoardIdSet.has(list.id)}
                    />
                    <div className="flex flex-1 flex-col">
                      <span className="text-sm font-medium">{list.title}</span>
                      <span className="text-muted-foreground text-xs">
                        {list.post_count}/{POSTS_PER_BOARD_LIMIT} posts
                      </span>
                      {fullBoardIdSet.has(list.id) && (
                        <span className="text-destructive text-xs">
                          Board is full. Create a new board to save more posts.
                        </span>
                      )}
                    </div>
                    {index === 0 && <Badge variant="outline">Recent</Badge>}
                  </label>
                ))}
              </RadioGroup>
              {!hasAvailableBoard && (
                <p className="text-muted-foreground text-xs">
                  All boards are full. Create a new board to continue.
                </p>
              )}
            </div>
          )}

          <Collapsible open={isCreateSectionOpen} onOpenChange={setIsCreateSectionOpen}>
            <div>
              <CollapsibleTrigger className="text-foreground hover:bg-muted flex w-full items-center justify-between rounded-md px-1 py-1 text-left text-sm font-medium">
                Create new board
                <ChevronDown className="text-muted-foreground h-4 w-4" />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 flex flex-col gap-2">
                <Input
                  placeholder="Type new board title"
                  value={newListTitle}
                  onChange={(e) => {
                    setNewListTitle(e.target.value);
                    if (e.target.value.trim().length > 0) {
                      setSelectedListId('');
                    }
                  }}
                />
                {isCreateOverLimit && (
                  <p className="text-destructive text-sm">
                    This board holds up to {POSTS_PER_BOARD_LIMIT} posts. Remove {overflowBy} to
                    continue.
                  </p>
                )}
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        <DialogFooter>
          <Button onClick={closeDialog} variant="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={
              isLoading ||
              postIds.length === 0 ||
              (!selectedListId && !isCreateMode) ||
              isCreateOverLimit
            }
          >
            Save to board
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
